import { NavLink } from 'react-router';
import type { Role, ViewKey } from '@/domain';
import { strings } from '@/shared/strings';
import { PersonIcon, ScanIcon, TrayIcon } from '../icons';
import { navItemsFor } from './navItems';
import { useShellStore } from './shellStore';
import styles from './BottomTabs.module.css';

export interface BottomTabsProps {
  role: Role;
  views: readonly ViewKey[];
  /** Whether this session may open the scanner, resolved from the session views. */
  canScan: boolean;
}

/** Five is what fits on a 360px phone without the labels colliding. */
const MAX_TABS = 5;

/**
 * The mobile tab bar. Section 5.1.
 *
 * For the two roles that work a bench it is three things, request of
 * 4 October 2026: the tray on the left, the scanner raised in the middle,
 * because scanning a tray is the single action those users perform most and it
 * should be reachable with a thumb, and their profile on the right. Everything
 * else they hold is in the menu.
 *
 * For everyone else it is derived from the same list as the sidebar, with the
 * scanner last for those who hold it.
 */
export function BottomTabs({ role, views, canScan }: BottomTabsProps) {
  const closeAll = useShellStore((state) => state.closeAll);
  const atBench = role === 'tech' || role === 'cadcam';

  const scanTab = (
    <NavLink
      key="scan"
      to="/scan"
      className={({ isActive }) => `${styles.tab} ${styles.scan} ${isActive ? styles.on : ''}`}
      onClick={closeAll}
    >
      <span className={styles.ic}>
        <ScanIcon />
      </span>
      <span className={styles.label}>{strings.shell.scan}</span>
    </NavLink>
  );

  if (atBench) {
    return (
      <nav className={styles.tabs} aria-label={strings.shell.menu}>
        <NavLink
          to="/queue"
          className={({ isActive }) => `${styles.tab} ${isActive ? styles.on : ''}`}
          onClick={closeAll}
        >
          <span className={styles.ic}>
            <TrayIcon />
          </span>
          <span className={styles.label}>{strings.queue.title}</span>
        </NavLink>
        {canScan && scanTab}
        <NavLink
          to="/profile"
          end
          className={({ isActive }) => `${styles.tab} ${isActive ? styles.on : ''}`}
          onClick={closeAll}
        >
          <span className={styles.ic}>
            <PersonIcon />
          </span>
          <span className={styles.label}>{strings.shell.profileTab}</span>
        </NavLink>
      </nav>
    );
  }

  /*
   * The scanner takes one of the five slots for everyone who holds it, so a
   * manager loses their fifth screen to it on a phone. That is the trade the
   * client asked for: scanning a tray to look at a case is worth more to a
   * manager walking the floor than the fifth entry of a ten entry menu.
   */
  const items = navItemsFor(views, true).slice(0, canScan ? MAX_TABS - 1 : MAX_TABS);
  const tabs = items.map((item) => (
    <NavLink
      key={item.view}
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) => `${styles.tab} ${isActive ? styles.on : ''}`}
      onClick={closeAll}
    >
      <span className={styles.ic}>{item.icon}</span>
      <span className={styles.label}>{item.label}</span>
    </NavLink>
  ));
  if (canScan) tabs.push(scanTab);

  return (
    <nav className={styles.tabs} aria-label={strings.shell.menu}>
      {tabs}
    </nav>
  );
}
